import { useState, useMemo } from "react";
import { AlertCircle, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, ExternalLink, Filter, Info, Search } from "lucide-react";
import type { DataRecord } from "../types";

interface SiteAuditPageIssuesPanelProps {
  pageIssues?: DataRecord[];
  crawledPages?: DataRecord[];
  clientSlug?: string;
}

export function SiteAuditPageIssuesPanel({
  pageIssues = [],
  crawledPages = [],
  clientSlug = "",
}: SiteAuditPageIssuesPanelProps) {
  const [selectedIssue, setSelectedIssue] = useState<string>("ALL");
  const [searchUrl, setSearchUrl] = useState<string>("");
  const [expandedUrl, setExpandedUrl] = useState<string | null>(null);

  // Mapear problemas por URL
  const issuesByUrl = useMemo(() => {
    const map = new Map<string, Array<{ issue_name: string; issue_count: number; issue_type?: string }>>();
    pageIssues.forEach((r) => {
      const url = String(r.url_raw || r.url_clean || r.page_url || "");
      if (!url) return;
      const list = map.get(url) || [];
      list.push({
        issue_name: String(r.issue_name || ""),
        issue_count: Number(r.issue_count || 1),
        issue_type: r.issue_type ? String(r.issue_type) : undefined,
      });
      map.set(url, list);
    });
    return map;
  }, [pageIssues]);

  // Catálogo de problemas únicos con conteo de URLs afectadas
  const uniqueIssues = useMemo(() => {
    const counts = new Map<string, { count: number; issue_type?: string }>();
    pageIssues.forEach((r) => {
      const issue = String(r.issue_name || "").trim();
      if (!issue) return;
      const current = counts.get(issue) || { count: 0, issue_type: r.issue_type ? String(r.issue_type) : undefined };
      current.count += 1;
      counts.set(issue, current);
    });
    return Array.from(counts.entries())
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.count - a.count);
  }, [pageIssues]);

  // Lista de URLs únicas afectadas
  const affectedUrls = useMemo(() => {
    const urls = Array.from(issuesByUrl.keys());
    return urls.filter((url) => {
      const matchesSearch = !searchUrl || url.toLowerCase().includes(searchUrl.toLowerCase());
      if (!matchesSearch) return false;
      if (selectedIssue === "ALL") return true;
      const issues = issuesByUrl.get(url) || [];
      return issues.some((i) => i.issue_name === selectedIssue);
    });
  }, [issuesByUrl, searchUrl, selectedIssue]);

  if (!pageIssues.length) {
    return (
      <div className="panel" style={{ padding: "20px", marginTop: "16px" }}>
        <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
          <Info size={22} style={{ color: "var(--accent, #6366f1)", flexShrink: 0, marginTop: "2px" }} />
          <div>
            <h4 style={{ margin: "0 0 6px 0", fontSize: "15px", fontWeight: 600 }}>
              Mega Export: Radiografía Detallada URL × Problema
            </h4>
            <p style={{ margin: 0, fontSize: "13px", color: "var(--muted, #8892b0)", lineHeight: 1.5 }}>
              Aún no se ha cargado el <strong>Mega Export de Páginas con problemas</strong> para este cliente.
              Para activar este diagnóstico quirúrgico por URL, dirígete a la sección <strong>Semrush → Carga Manual</strong>,
              selecciona el reporte <strong>Site Audit — Mega Export</strong> y sube el archivo{" "}
              <em>"Páginas rastreadas con todos los problemas"</em> exportado desde Semrush.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="panel" style={{ marginTop: "20px" }}>
      <div className="panel-heading">
        <div>
          <span className="eyebrow">Semrush Site Audit · Mega Export</span>
          <h3>Radiografía URL × Problemas Específicos</h3>
          <p className="panel-copy">
            Matriz cruzada con {pageIssues.length.toLocaleString()} incidencias técnicas detectadas en {issuesByUrl.size} URLs.
            Filtra por problema o busca una landing page específica para auditar sus fallas.
          </p>
        </div>
      </div>

      {/* Barra de filtros interactiva */}
      <div style={{ padding: "0 20px 16px 20px", display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: "1 1 280px" }}>
          <Filter size={15} style={{ color: "var(--muted, #8892b0)" }} />
          <select
            className="compact-select"
            style={{ width: "100%", fontSize: "13px" }}
            value={selectedIssue}
            onChange={(e) => setSelectedIssue(e.target.value)}
          >
            <option value="ALL">Todos los problemas ({uniqueIssues.length} tipos)</option>
            {uniqueIssues.map((issue) => (
              <option key={issue.name} value={issue.name}>
                {issue.name} ({issue.count} URLs)
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: "1 1 260px" }}>
          <Search size={15} style={{ color: "var(--muted, #8892b0)" }} />
          <input
            type="text"
            className="compact-input"
            style={{ width: "100%", fontSize: "13px" }}
            placeholder="Buscar por URL o path..."
            value={searchUrl}
            onChange={(e) => setSearchUrl(e.target.value)}
          />
        </div>

        {(selectedIssue !== "ALL" || searchUrl) && (
          <button
            type="button"
            className="button button-ghost"
            style={{ fontSize: "12px", padding: "4px 8px" }}
            onClick={() => {
              setSelectedIssue("ALL");
              setSearchUrl("");
            }}
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Lista de URLs con problemas expandibles */}
      <div style={{ padding: "0 20px 20px 20px" }}>
        {affectedUrls.length === 0 ? (
          <p style={{ color: "var(--muted, #8892b0)", fontSize: "13px", padding: "16px 0", textAlign: "center" }}>
            No se encontraron URLs que coincidan con los filtros seleccionados.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {affectedUrls.slice(0, 50).map((url) => {
              const issues = issuesByUrl.get(url) || [];
              const isExpanded = expandedUrl === url;
              const errorCount = issues.filter((i) => i.issue_type === "ERROR").length;
              const warningCount = issues.filter((i) => i.issue_type === "WARNING").length;

              return (
                <div
                  key={url}
                  style={{
                    border: "1px solid var(--border-soft, #1e222d)",
                    borderRadius: "6px",
                    background: isExpanded ? "rgba(255, 255, 255, 0.02)" : "transparent",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      padding: "10px 14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      gap: "12px",
                    }}
                    onClick={() => setExpandedUrl(isExpanded ? null : url)}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0, flex: 1 }}>
                      {isExpanded ? (
                        <ChevronDown size={16} style={{ color: "var(--muted, #8892b0)", flexShrink: 0 }} />
                      ) : (
                        <ChevronRight size={16} style={{ color: "var(--muted, #8892b0)", flexShrink: 0 }} />
                      )}
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: 500,
                          fontFamily: "monospace",
                          color: "var(--text, #e2e8f0)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={url}
                      >
                        {url}
                      </span>
                      <a
                        href={url.startsWith("http") ? url : `https://${url}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        title="Abrir URL en pestaña nueva"
                        style={{ color: "var(--muted, #8892b0)", display: "flex", alignItems: "center" }}
                      >
                        <ExternalLink size={13} />
                      </a>
                    </div>

                    <div style={{ display: "flex", gap: "6px", alignItems: "center", flexShrink: 0 }}>
                      {errorCount > 0 && (
                        <span className="badge badge-danger" style={{ fontSize: "11px", padding: "2px 6px" }}>
                          {errorCount} {errorCount === 1 ? "error" : "errores"}
                        </span>
                      )}
                      {warningCount > 0 && (
                        <span className="badge badge-warning" style={{ fontSize: "11px", padding: "2px 6px" }}>
                          {warningCount} adv.
                        </span>
                      )}
                      <span className="badge badge-muted" style={{ fontSize: "11px", padding: "2px 6px" }}>
                        {issues.length} {issues.length === 1 ? "incidencia" : "incidencias"}
                      </span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div
                      style={{
                        padding: "10px 14px 14px 38px",
                        borderTop: "1px solid var(--border-soft, #1e222d)",
                        background: "rgba(0, 0, 0, 0.15)",
                      }}
                    >
                      <div style={{ fontSize: "12px", color: "var(--muted, #8892b0)", marginBottom: "8px" }}>
                        Incidencias registradas en esta URL:
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                        {issues.map((issue, idx) => {
                          const isErr = issue.issue_type === "ERROR";
                          const isWarn = issue.issue_type === "WARNING";
                          const borderCol = isErr ? "#ef4444" : isWarn ? "#f59e0b" : "#6366f1";
                          const bgCol = isErr
                            ? "rgba(239, 68, 68, 0.1)"
                            : isWarn
                            ? "rgba(245, 158, 11, 0.1)"
                            : "rgba(99, 102, 241, 0.1)";

                          return (
                            <span
                              key={idx}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "5px",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                fontSize: "12px",
                                border: `1px solid ${borderCol}`,
                                background: bgCol,
                                color: "var(--text, #e2e8f0)",
                              }}
                            >
                              {isErr ? (
                                <AlertCircle size={13} style={{ color: "#ef4444" }} />
                              ) : isWarn ? (
                                <AlertTriangle size={13} style={{ color: "#f59e0b" }} />
                              ) : (
                                <Info size={13} style={{ color: "#6366f1" }} />
                              )}
                              <span>{issue.issue_name}</span>
                              {issue.issue_count > 1 && (
                                <strong style={{ color: borderCol, marginLeft: "2px" }}>
                                  ({issue.issue_count})
                                </strong>
                              )}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {affectedUrls.length > 50 && (
              <p style={{ textAlign: "center", fontSize: "12px", color: "var(--muted, #8892b0)", margin: "12px 0 0 0" }}>
                Mostrando las primeras 50 URLs de {affectedUrls.length}. Usa el buscador para filtrar.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
